// =====================================================================
//  COLUMNAS AJUSTABLES  (18/10, sesión 132, D196)
// =====================================================================
//  Una `table.t-ajustable` arranca con cada columna del ancho justo de lo que
//  muestra (sin cortar renglones). Arrastrando el borde derecho de un título
//  se cambia el ancho de esa columna; con doble clic en ese borde vuelve al
//  mínimo para que se lea todo. Los anchos los recuerda el navegador (por
//  tabla, en `localStorage`): si el navegador no deja guardar, se usan igual
//  mientras dure la pantalla.
//
//  La pantalla repinta las tablas con innerHTML: después de cada repintada se
//  llama a `ajustable(tabla, clave)`, que arma el `<colgroup>`, aplica los
//  anchos guardados y pone las asas.

const CLAVE = (k: string) => 'nubilon.anchos.' + k;
const EN_MEMORIA: Record<string, (number | null)[]> = {};

function leer(clave: string): (number | null)[] {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE(clave)) ?? 'null');
    if (Array.isArray(v)) return v.map(x => (typeof x === 'number' && x > 0 ? x : null));
  } catch { /* sin almacenamiento: queda en memoria */ }
  return EN_MEMORIA[clave] ?? [];
}
function guardar(clave: string, anchos: (number | null)[]){
  EN_MEMORIA[clave] = anchos;
  try { localStorage.setItem(CLAVE(clave), JSON.stringify(anchos)); } catch { /* sin almacenamiento */ }
}

//  Lo que ocupa el contenido de una celda (sin el asa), más sus márgenes. Se
//  mide el contenido y no la celda: una celda más ancha que lo que muestra
//  dice su propio ancho.
function anchoContenido(c: HTMLTableCellElement): number {
  let izq = Infinity, der = -Infinity;
  const r = document.createRange();
  for (const n of Array.from(c.childNodes)){
    if (n instanceof HTMLElement && n.classList.contains('asa')) continue;
    if (n.nodeType === Node.TEXT_NODE && !(n.textContent ?? '').trim()) continue;
    r.selectNodeContents(n);
    const b = n instanceof Element ? n.getBoundingClientRect() : r.getBoundingClientRect();
    if (!b.width) continue;
    izq = Math.min(izq, b.left); der = Math.max(der, b.right);
  }
  const cs = getComputedStyle(c);
  const bordes = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
  return (der > izq ? der - izq : 0) + bordes;
}

//  El ancho mínimo para que se lea todo lo de la columna `i`: el más ancho de
//  sus celdas (las de una sola columna; las que abarcan varias no cuentan).
function anchoJusto(t: HTMLTableElement, i: number): number {
  let max = 40;
  for (const tr of Array.from(t.rows)){
    let col = 0;
    for (const c of Array.from(tr.cells)){
      if (col === i && c.colSpan === 1) max = Math.max(max, anchoContenido(c));
      col += c.colSpan || 1;
      if (col > i) break;
    }
  }
  return Math.ceil(max) + 2;
}

function aplicar(t: HTMLTableElement, anchos: (number | null)[]){
  const cols = Array.from(t.querySelectorAll(':scope > colgroup > col')) as HTMLTableColElement[];
  const fija = anchos.some(a => a != null);
  t.classList.toggle('fija', fija);
  if (!fija){ cols.forEach(c => { c.style.width = ''; }); t.style.width = ''; return; }
  cols.forEach((c, i) => { c.style.width = (anchos[i] ?? 0) + 'px'; });
  t.style.width = anchos.reduce<number>((s, a) => s + (a ?? 0), 0) + 'px';
}

export function ajustable(t: HTMLTableElement | null, clave: string){
  if (!t) return;
  const ths = Array.from(t.querySelectorAll(':scope > thead > tr:first-child > th')) as HTMLTableCellElement[];
  if (!ths.length) return;
  t.classList.add('t-ajustable');
  if (!t.querySelector(':scope > colgroup')){
    const cg = document.createElement('colgroup');
    ths.forEach(() => cg.appendChild(document.createElement('col')));
    t.insertBefore(cg, t.firstChild);
  }
  const guardados = leer(clave);
  //  Lo guardado sólo vale si la tabla tiene las mismas columnas.
  const anchos: (number | null)[] = guardados.length === ths.length ? [...guardados] : ths.map(() => null);
  //  Con algún ancho guardado, las demás columnas toman su ancho justo.
  if (anchos.some(a => a != null)) ths.forEach((_, i) => { if (anchos[i] == null) anchos[i] = anchoJusto(t, i); });
  aplicar(t, anchos);

  ths.forEach((th, i) => {
    if (th.querySelector(':scope > .asa')) return;
    const asa = document.createElement('span');
    asa.className = 'asa';
    asa.title = 'Arrastrá para cambiar el ancho · doble clic: el justo para que se lea todo';
    asa.setAttribute('aria-hidden', 'true');
    th.appendChild(asa);
    //  Que tocar el asa no ordene la tabla ni haga nada más.
    asa.addEventListener('click', e => { e.stopPropagation(); e.preventDefault(); });
    asa.addEventListener('pointerdown', e => {
      e.preventDefault(); e.stopPropagation();
      //  Al empezar a arrastrar, todas las columnas quedan fijas en lo que miden hoy.
      const actuales = ths.map(h => Math.round(h.getBoundingClientRect().width));
      const x0 = e.clientX, w0 = actuales[i];
      try { asa.setPointerCapture(e.pointerId); } catch { /* sin captura */ }
      const mover = (ev: PointerEvent) => {
        actuales[i] = Math.max(40, Math.round(w0 + ev.clientX - x0));
        aplicar(t, actuales);
      };
      const soltar = () => {
        asa.removeEventListener('pointermove', mover);
        asa.removeEventListener('pointerup', soltar);
        asa.removeEventListener('pointercancel', soltar);
        guardar(clave, actuales);
      };
      asa.addEventListener('pointermove', mover);
      asa.addEventListener('pointerup', soltar);
      asa.addEventListener('pointercancel', soltar);
    });
    asa.addEventListener('dblclick', e => {
      e.preventDefault(); e.stopPropagation();
      const actuales = ths.map(h => Math.round(h.getBoundingClientRect().width));
      actuales[i] = anchoJusto(t, i);
      aplicar(t, actuales);
      guardar(clave, actuales);
    });
  });
}
